import { parsePhoneNumberWithError, AsYouType } from "libphonenumber-js/max";

/**
 * @typedef {import('libphonenumber-js/max').PhoneNumber} PhoneNumber
 */

// Country code mapping with display names
export const COUNTRY_PHONE_PREFIXES = {
  India: {
    code: "IN",
    prefix: "+91",
  },
};

/**
 * Get country prefix by country name
 * @param {string} countryName - The name of the country
 * @returns {string|undefined} - The country prefix with '+' or undefined if not found
 */
export const getCountryPrefix = (countryName) => {
  return COUNTRY_PHONE_PREFIXES[countryName]?.prefix;
};

/**
 * Get country code by country name
 * @param {string} countryName - The name of the country
 * @returns {string|undefined} - The country code (e.g., 'IN', 'US') or undefined if not found
 */
export const getCountryCode = (countryName) => {
  return COUNTRY_PHONE_PREFIXES[countryName]?.code;
};

/**
 * Parse a phone number and return the parsed object
 * @param {string} phoneNumber - The phone number to parse
 * @param {string} countryName - The country name to get the country code
 * @param {boolean} isPhoneNumberOnly - Whether the phone number is only the digits without the country code
 * @returns {PhoneNumber|null} - The parsed phone number object or null if parsing failed
 */
const parsePhoneNumberObject = (
  phoneNumber,
  countryName,
  isPhoneNumberOnly = false
) => {
  if (!phoneNumber) return null;

  const countryCode = getCountryCode(countryName);
  if (!countryCode) return null;

  try {
    // If this is just the phone number part (without country code)
    if (isPhoneNumberOnly) {
      const prefix = getCountryPrefix(countryName);
      if (!prefix) return null;
      return parsePhoneNumberWithError(prefix + phoneNumber, countryCode);
    }

    // Parse the full phone number
    return parsePhoneNumberWithError(phoneNumber, countryCode);
  } catch (error) {
    return null;
  }
};

/**
 * Validate phone number using a parsed phone number object
 * @param {Object} parsedNumber - The parsed phone number object
 * @param {string} countryName - The country name (for country-specific validation)
 * @returns {boolean} - Whether the phone number is valid
 */
const validatePhoneNumberFromParsed = (parsedNumber, countryName) => {
  if (!parsedNumber) return false;

  // Basic validation
  const isValid = parsedNumber.isValid();

  // Special validation for India
  if (countryName === "India" && isValid) {
    const phoneType = parsedNumber.getType();
    return phoneType === "MOBILE" || phoneType === "FIXED_LINE_OR_MOBILE";
  }

  return isValid;
};

/**
 * Process a phone number and return validation and formatting results
 * @param {string} phoneNumber - The phone number to process
 * @param {string} countryName - The country name
 * @param {boolean} isPhoneNumberOnly - Whether the phone number is only the digits without the country code
 * @returns {Object} - Object containing { isValid, e164Number }
 */
export const processPhoneNumber = (
  phoneNumber,
  countryName,
  isPhoneNumberOnly = false
) => {
  const parsedNumber = parsePhoneNumberObject(
    phoneNumber,
    countryName,
    isPhoneNumberOnly
  );

  if (!parsedNumber) {
    return { isValid: false, e164Number: "" };
  }

  const isValid = validatePhoneNumberFromParsed(parsedNumber, countryName);
  const e164Number = parsedNumber.number;

  return { isValid, e164Number };
};

/**
 * Format phone number as you type
 * @param {string} phoneNumber - The phone number being typed
 * @param {string} countryName - The country name to get the country code
 * @returns {string} - The formatted phone number
 */
export const formatAsYouType = (phoneNumber, countryName) => {
  if (!phoneNumber) return "";

  const countryCode = getCountryCode(countryName);
  if (!countryCode) return phoneNumber;

  try {
    return new AsYouType(countryCode).input(phoneNumber);
  } catch (error) {
    return phoneNumber;
  }
};

/**
 * Validate phone number in real-time as the user types
 * @param {string} phoneNumber - The phone number being typed
 * @param {string} countryName - The country name to get the country code
 * @returns {boolean} - Whether the phone number is valid
 */
export const validatePhoneNumberRealTime = (phoneNumber, countryName) => {
  if (!phoneNumber || phoneNumber.length === 0) {
    // Empty input is considered valid during typing
    return true;
  }

  const countryCode = getCountryCode(countryName);
  if (!countryCode) {
    return false;
  }

  try {
    const formatter = new AsYouType(countryCode);
    formatter.input(phoneNumber);

    // A phone number is valid if:
    // 1. It's valid according to the formatter
    // 2. The detected country matches the expected country (if a country is detected)
    // 3. It's not in international format (doesn't start with +)
    const isValid = formatter.isValid();
    const detectedCountry = formatter.getCountry();
    const isInternational = formatter.isInternational();

    return (
      isValid &&
      (!detectedCountry || detectedCountry === countryCode) &&
      !isInternational
    );
  } catch (error) {
    return false;
  }
};

export const isLikelyPhoneNumber = (str) => {
  // Simple regex to check if string looks like a phone number
  return /^\+?[\d\s-]{7,15}$/.test(str);
};
